import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import SettingsPageShell from './SettingsPageShell';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { retailer } from '../../utils/api';
import { toast } from 'sonner';
import { 
  CreditCard, Building2, DollarSign, CheckCircle, Clock, 
  AlertCircle, ExternalLink, RefreshCw, Wallet, TrendingUp,
  ArrowRight, Shield, Zap, Calendar, Receipt
} from 'lucide-react';
import { HelpTooltip } from '../shared/HelpTooltip';

export default function RetailerPayout({ user, onLogout }) {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [payoutStatus, setPayoutStatus] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);
  const [bankFormVisible, setBankFormVisible] = useState(false);
  const [bankDetails, setBankDetails] = useState({
    legal_name: '',
    ein_tax_id: '',
    bank_name: '',
    bank_routing_number: '',
    bank_account_number: '',
    account_type: 'checking'
  });
  const [bankLoading, setBankLoading] = useState(false);

  useEffect(() => {
    loadPayoutStatus();
    
    // Check for Stripe redirect params
    if (searchParams.get('stripe_success') === 'true') {
      toast.success('Stripe Connect setup complete! Checking account status...');
      setTimeout(loadPayoutStatus, 1500);
    } else if (searchParams.get('stripe_refresh') === 'true') {
      toast.info('Please complete your Stripe account setup');
    }
  }, [searchParams]);

  const loadPayoutStatus = async () => {
    try {
      setLoading(true);
      const response = await retailer.getPayoutStatus();
      setPayoutStatus(response.data);
    } catch (error) {
      toast.error('Failed to load payout status');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleStripeConnect = async () => {
    try {
      setConnectLoading(true);
      const response = await retailer.createStripeConnectLink();
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create Stripe Connect link');
    } finally {
      setConnectLoading(false);
    }
  };

  const handleRefreshStripeStatus = async () => {
    try {
      setConnectLoading(true);
      await retailer.getStripeStatus();
      await loadPayoutStatus();
      toast.success('Stripe status refreshed');
    } catch (error) {
      toast.error('Failed to refresh status');
    } finally {
      setConnectLoading(false);
    }
  };

  const handleBankSubmit = async (e) => {
    e.preventDefault();
    
    // Validate
    if (!bankDetails.legal_name || !bankDetails.bank_name || 
        !bankDetails.bank_routing_number || !bankDetails.bank_account_number) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (bankDetails.bank_routing_number.length !== 9) {
      toast.error('Routing number must be 9 digits');
      return;
    }

    try {
      setBankLoading(true);
      await retailer.submitBankDetails(bankDetails);
      toast.success('Bank details saved successfully');
      setBankFormVisible(false);
      loadPayoutStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save bank details');
    } finally {
      setBankLoading(false);
    }
  };

  const isStripeConnected = payoutStatus?.stripe_account_id && payoutStatus?.stripe_payouts_enabled;
  const hasBankDetails = payoutStatus?.manual_bank_details;
  const hasPaymentMethod = isStripeConnected || hasBankDetails;

  return (
    <SettingsPageShell user={user} onLogout={onLogout} title="Payout" subtitle="Stripe Connect, bank account, schedule & fees">
      <div className="space-y-6">

        {/* Earnings Summary */}
        <div className="grid md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="bg-green-100 p-3 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Earnings</p>
                  <p className="text-2xl font-bold text-green-600">
                    ${loading ? '...' : (payoutStatus?.total_earnings?.toFixed(2) || '0.00')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="bg-yellow-100 p-3 rounded-lg">
                  <Wallet className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Pending Payout</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    ${loading ? '...' : (payoutStatus?.pending_payout?.toFixed(2) || '0.00')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <DollarSign className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Paid Out</p>
                  <p className="text-2xl font-bold text-blue-600">
                    ${loading ? '...' : (payoutStatus?.total_paid?.toFixed(2) || '0.00')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Payment Method Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-600" />
              Payment Method
            </CardTitle>
            <CardDescription>
              Set up how you'd like to receive your earnings from DealShaq
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Current Status */}
            {hasPaymentMethod && (
              <div className={`p-4 rounded-lg border ${isStripeConnected ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}`}>
                <div className="flex items-center gap-3">
                  <CheckCircle className={`w-5 h-5 ${isStripeConnected ? 'text-green-600' : 'text-blue-600'}`} />
                  <div>
                    <p className={`font-medium ${isStripeConnected ? 'text-green-800' : 'text-blue-800'}`}>
                      {isStripeConnected ? 'Stripe Connect Active' : 'Manual Bank Transfer'}
                    </p>
                    <p className={`text-sm ${isStripeConnected ? 'text-green-600' : 'text-blue-600'}`}>
                      {isStripeConnected 
                        ? 'Automatic payouts enabled'
                        : `Bank: ${payoutStatus?.manual_bank_details?.bank_name} (****${payoutStatus?.manual_bank_details?.account_last4})`
                      }
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Stripe Connect Option */}
            <div className={`border rounded-xl p-6 ${isStripeConnected ? 'border-green-300 bg-green-50' : 'border-gray-200'}`}>
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-lg ${isStripeConnected ? 'bg-green-200' : 'bg-purple-100'}`}>
                  <CreditCard className={`w-6 h-6 ${isStripeConnected ? 'text-green-700' : 'text-purple-600'}`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-semibold text-gray-900">Stripe Connect</h4>
                    <Badge className="bg-purple-100 text-purple-800">Recommended</Badge>
                    {isStripeConnected && <Badge className="bg-green-100 text-green-800">Active</Badge>}
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Fast, secure, and automatic payouts directly to your bank account. 
                    No need to share bank details with DealShaq.
                  </p>
                  
                  <div className="flex flex-wrap gap-4 mb-4 text-sm">
                    <span className="flex items-center gap-1 text-gray-600">
                      <Zap className="w-4 h-4 text-yellow-500" /> Instant setup
                    </span>
                    <span className="flex items-center gap-1 text-gray-600">
                      <Shield className="w-4 h-4 text-green-500" /> Bank-level security
                    </span>
                    <span className="flex items-center gap-1 text-gray-600">
                      <Clock className="w-4 h-4 text-blue-500" /> 2-day payouts
                    </span>
                  </div>

                  {isStripeConnected ? (
                    <div className="flex gap-3">
                      <Button
                        variant="outline"
                        onClick={handleRefreshStripeStatus}
                        disabled={connectLoading}
                        data-testid="refresh-stripe-status"
                      >
                        {connectLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                        Refresh Status
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handleStripeConnect}
                        disabled={connectLoading}
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Manage in Stripe
                      </Button>
                    </div>
                  ) : (
                    <Button
                      onClick={handleStripeConnect}
                      disabled={connectLoading}
                      className="bg-purple-600 hover:bg-purple-700"
                      data-testid="connect-stripe-btn"
                    >
                      {connectLoading ? (
                        <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <ArrowRight className="w-4 h-4 mr-2" />
                      )}
                      {connectLoading ? 'Connecting...' : 'Connect with Stripe'}
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Manual Bank Option */}
            <div className={`border rounded-xl p-6 ${hasBankDetails && !isStripeConnected ? 'border-blue-300 bg-blue-50' : 'border-gray-200'}`}>
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-lg ${hasBankDetails && !isStripeConnected ? 'bg-blue-200' : 'bg-gray-100'}`}>
                  <Building2 className={`w-6 h-6 ${hasBankDetails && !isStripeConnected ? 'text-blue-700' : 'text-gray-600'}`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-semibold text-gray-900">Manual Bank Transfer</h4>
                    {hasBankDetails && !isStripeConnected && <Badge className="bg-blue-100 text-blue-800">Active</Badge>}
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Provide your bank details for ACH transfers. Payouts are processed weekly 
                    and may take 3-5 business days to arrive.
                  </p>
                  <div className="flex items-start gap-2 mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200">
                    <Clock className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-amber-900">
                      <strong>Manual transfers run every Friday.</strong> For instant 2-day automatic payouts, we recommend Stripe Connect (above). Your account number is encrypted at rest.
                    </p>
                  </div>

                  {!bankFormVisible ? (
                    <Button
                      variant="outline"
                      onClick={() => setBankFormVisible(true)}
                      data-testid="add-bank-btn"
                    >
                      {hasBankDetails ? 'Update Bank Details' : 'Add Bank Account'}
                    </Button>
                  ) : (
                    <form onSubmit={handleBankSubmit} className="space-y-4 max-w-md">
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">Legal Business Name *</label>
                        <Input
                          value={bankDetails.legal_name}
                          onChange={(e) => setBankDetails({...bankDetails, legal_name: e.target.value})}
                          placeholder="Your Store LLC"
                          data-testid="bank-legal-name"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">EIN / Tax ID</label>
                        <Input
                          value={bankDetails.ein_tax_id}
                          onChange={(e) => setBankDetails({...bankDetails, ein_tax_id: e.target.value})}
                          placeholder="XX-XXXXXXX"
                          data-testid="bank-ein"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">Bank Name *</label>
                        <Input
                          value={bankDetails.bank_name}
                          onChange={(e) => setBankDetails({...bankDetails, bank_name: e.target.value})}
                          placeholder="Bank of America"
                          data-testid="bank-name"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-gray-700 mb-1 block">Routing Number *</label>
                          <Input
                            value={bankDetails.bank_routing_number}
                            onChange={(e) => setBankDetails({...bankDetails, bank_routing_number: e.target.value.replace(/\D/g, '').slice(0, 9)})}
                            placeholder="9 digits"
                            maxLength={9}
                            data-testid="bank-routing"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-700 mb-1 block">Account Number *</label>
                          <Input
                            value={bankDetails.bank_account_number}
                            onChange={(e) => setBankDetails({...bankDetails, bank_account_number: e.target.value.replace(/\D/g, '')})}
                            placeholder="Account number"
                            type="password"
                            data-testid="bank-account"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">Account Type *</label>
                        <select
                          value={bankDetails.account_type}
                          onChange={(e) => setBankDetails({...bankDetails, account_type: e.target.value})}
                          className="w-full px-3 py-2 border rounded-md"
                          data-testid="bank-account-type"
                        >
                          <option value="checking">Checking</option>
                          <option value="savings">Savings</option>
                        </select>
                      </div>
                      <div className="flex gap-3">
                        <Button
                          type="submit"
                          disabled={bankLoading}
                          className="bg-blue-600 hover:bg-blue-700"
                          data-testid="save-bank-btn"
                        >
                          {bankLoading ? 'Saving...' : 'Save Bank Details'}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setBankFormVisible(false)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Payouts */}
        {payoutStatus?.recent_payouts?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                Recent Payouts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {payoutStatus.recent_payouts.map((payout, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${payout.method === 'stripe_connect' ? 'bg-purple-100' : 'bg-blue-100'}`}>
                        {payout.method === 'stripe_connect' 
                          ? <CreditCard className="w-4 h-4 text-purple-600" />
                          : <Building2 className="w-4 h-4 text-blue-600" />
                        }
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">${payout.amount?.toFixed(2)}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(payout.date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <Badge className={payout.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                      {payout.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* DealShaq Financial Info — Read-Only */}
        <Card className="border-0 shadow-md bg-slate-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-slate-700" />
              Payout Schedule
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-700 leading-relaxed" data-testid="payout-schedule-text">
              <strong>Stripe Connect Retailers:</strong> Payouts are automatically sent by Stripe on a rolling 2-day schedule.<br />
              <strong>Manual Bank Retailers:</strong> DealShaq processes payouts every Friday at 9:00 AM ET.
            </p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md bg-slate-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-slate-700" />
              Fees & Commissions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-700 leading-relaxed" data-testid="fees-commissions-text">
              DealShaq retains a platform margin that varies by deal tier (about <strong>10-15% of each item's regular price</strong>) to support platform operations, payment processing, and charity contributions.
            </p>
          </CardContent>
        </Card>

      </div>
    </SettingsPageShell>
  );
}
